/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import { bool, func, number, string } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  AvatarLarge,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  OwnListingLink,
} from '../../components';

import css from './TopbarMobileMenu.module.css';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUserListing,
    currentUserListingFetched,
    currentUser,
    notificationCount,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
        </div>
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };
  const isProvider = currentUser && currentUser?.attributes?.profile.publicData.isProvider;

  const isSuperAdmin = currentUser && currentUser?.attributes?.profile?.publicData?.isSuperAdmin;
  const testSuperAdmin =
    currentUser && currentUser?.id?.uuid === '650c0156-3a65-4f3c-a029-1ef032534299';
  const employerLink = isAuthenticated ? (
    <NamedLink
      className={css.createListingLink}
      activeClassName={css.activeCreateListingLink}
      // name="CMSPage"
      name="LookingForStaff"
      // params={{ pageId: 'business' }}
    >
      <FormattedMessage id="TopbarDesktop.lookingForStaff" />
    </NamedLink>
  ) : null;

  const employeeLink = isAuthenticated ? (
    <NamedLink
      className={css.createListingLink}
      activeClassName={css.activeCreateListingLink}
      // name="CMSPage"
      name="LookingForWork"
      // params={{ pageId: 'workers' }}
    >
      <FormattedMessage id="TopbarDesktop.lookingForWork" />
    </NamedLink>
  ) : null;
  const blogLink = (
    <NamedLink
      className={css.createListingLink}
      activeClassName={css.activeCreateListingLink}
      name="CMSPage"
      params={{ pageId: 'blog' }}
    >
      <FormattedMessage id="TopbarDesktop.blog" />
    </NamedLink>
  );
  const aboutLink = (
    <NamedLink
      className={css.createListingLink}
      activeClassName={css.activeCreateListingLink}
      name="AboutUs"
      params={{ pageId: 'about' }}
    >
      <FormattedMessage id="TopbarDesktop.aboutus" />
    </NamedLink>
  );
  const HomeLink =
    isAuthenticated && isSuperAdmin ? (
      <NamedLink
        className={css.createListingLink}
        name="AdminStaffPage"
        activeClassName={css.activeCreateListingLink}
      >
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>
    ) : isAuthenticated && isProvider ? (
      <NamedLink
        className={css.createListingLink}
        name="BusinessDashboardStaffPage"
        activeClassName={css.activeCreateListingLink}
      >
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>
    ) : isAuthenticated && !isProvider ? (
      <NamedLink
        className={css.createListingLink}
        activeClassName={css.activeCreateListingLink}
        name="SearchPage"
      >
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>
    ) : (
      <NamedLink
        className={css.createListingLink}
        activeClassName={css.activeCreateListingLink}
        name="LandingPage"
      >
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>
    );

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        {isProvider && !testSuperAdmin && (
          <div className={css.footer}>
            <NamedLink className={css.createNewListingLink} name="NewListingPage">
              <FormattedMessage id="TopbarMobileMenu.newListingLink" />
            </NamedLink>
          </div>
        )}
        <div className={css.Landingpage}>
        {HomeLink}
        </div>

        <div className={css.CompanyLinkWrap}>
        {!!isProvider ? (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfilePage'))}
            name="ProfilePage"
            params={{ id: currentUser?.id.uuid }}
          >
            <FormattedMessage id="TopbarDesktop.profilePageLink" />
          </NamedLink>
        ) : (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
        )}
        {isProvider && !testSuperAdmin && (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
            params={{ id: currentUser?.id.uuid }}
          >
            <FormattedMessage id="TopbarDesktop.Listings" />
          </NamedLink>
        )}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('MessagePage'))}
          name="MessagePage"
          // params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
        >
          {/* <span className={css.inbox}> */}
          <FormattedMessage id="TopbarDesktop.inbox" />
          {/* {notificationDot} */}
          {/* </span> */}
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>
        {isProvider && !testSuperAdmin && (
          <NamedLink
            className={classNames(css.inbox, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
        )}
        {!isProvider && !testSuperAdmin && (
          <NamedLink
            className={classNames(css.inbox, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: 'orders' }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
        )}
        </div>
        
        <div className={css.otherLinkWrap}>
        {employerLink}
        {employeeLink}
        {blogLink}
        {aboutLink}
        </div>
        {/* <OwnListingLink
          listing={currentUserListing}
          listingFetched={currentUserListingFetched}
          className={css.navigationLink}
        /> */}

        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = {
  currentUser: null,
  notificationCount: 0,
  currentPage: null,
  currentUserListing: null,
  currentUserListingFetched: false,
};

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
